import React from 'react';
import styles from './Commitments.module.scss';
import Loading from '../../../Common/Loading/Loading';
import Error, { getErrorComponentFromHttpError } from '../../../Common/Error/Error';
import Button from '@mui/material/Button';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { attendanceSortFn } from '../../../Common/MeetingDetailsView/MeetingDetailsView';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { getWeekBoundsForDate } from '../../../../../util/formatDate';
import { AdministratorEditMeetingCommitmentDialog } from '../../../Common/EditMeetingCommitmentDialog/EditMeetingCommitmentDialog';
import { v4 as uuid } from 'uuid';
import CommitmentsTeamMember from '../../../Common/MeetingDetailsView/CommitmentsTeamMember/CommitmentsTeamMember';
import { DummyUserPortrait } from '../../../User/Team/Meetings/Meeting/UserPortrait/UserPortrait';
import CommitmentsTableCommitment from '../../../Common/MeetingDetailsView/CommitmentsTableCommitment/CommitmentsTableCommitment';
import { GroupsOutlined } from '@mui/icons-material';
import LaunchIcon from '@mui/icons-material/Launch';
import { Link } from 'react-router-dom';
import useApiData from '../../../../../hooks/useApiData';
import useAuth from '../../../../../hooks/useAuth';

export default function Commitments() {
  const { groups } = useAuth();
  const [selectedWeek, setSelectedWeek] = React.useState(getWeekBoundsForDate());
  const [selectedCommitment, setSelectedCommitment] = React.useState(null);
  const [selectedMeeting, setSelectedMeeting] = React.useState(null);
  const [isEditMeetingCommitmentDialogOpen, setIsEditMeetingCommitmentDialogOpen] = React.useState(false);
  const { data, error, isLoading, reload } = useApiData({
    path: '/administrator/commitments',
    params: {
      includeTeam: true,
      includeTeamGoals: true,
      includeTeamMembers: true,
      includeCommitments: true,
      includeAttendances: true,
      fromDate: selectedWeek.minTime.toISOString(),
      toDate: selectedWeek.maxTime.toISOString(),
    },
  });

  function incrementSelectedWeek() {
    setSelectedWeek((currentlySelectedWeek) => {
      const d = new Date(currentlySelectedWeek.maxTime).setUTCDate(currentlySelectedWeek.maxTime.getUTCDate() + 1);
      return getWeekBoundsForDate(d);
    });
  }
  function decrementSelectedWeek() {
    setSelectedWeek((currentlySelectedWeek) => {
      const d = new Date(currentlySelectedWeek.minTime).setUTCDate(currentlySelectedWeek.minTime.getUTCDate() - 1);
      return getWeekBoundsForDate(d);
    });
  }

  React.useEffect(() => {
    reload();
  }, [selectedWeek]);

  if (isLoading) {
    return <Loading message="Fetching data ..." />;
  }

  if (error) {
    return getErrorComponentFromHttpError(error);
  }

  return (
    <>
      {selectedMeeting && selectedCommitment && (
        <AdministratorEditMeetingCommitmentDialog
          meeting={selectedMeeting}
          commitment={selectedCommitment}
          isOpen={isEditMeetingCommitmentDialogOpen}
          close={() => {
            setIsEditMeetingCommitmentDialogOpen(false);
            setSelectedCommitment(null);
          }}
          reload={reload}
        />
      )}
      <div className={styles.commitments}>
        <div className={styles.header}>
          <h1>Commitments</h1>
          <div className={styles.weekSelector}>
            <Button variant="text" onClick={decrementSelectedWeek}>
              <ArrowBackIcon />
            </Button>
            <p>
              {selectedWeek.minTime
                .toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'long',
                  timeZone: 'UTC',
                  ...(selectedWeek.minTime.getUTCFullYear() === selectedWeek.maxTime.getUTCFullYear()
                    ? {}
                    : { year: 'numeric' }),
                })
                .replaceAll(/ /g, '\u00A0')}
              {'\u00A0to\u00A0'}
              {selectedWeek.maxTime
                .toLocaleDateString('en-GB', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  timeZone: 'UTC',
                })
                .replaceAll(/ /g, '\u00A0')}
            </p>
            <Button variant="text" onClick={incrementSelectedWeek}>
              <ArrowForwardIcon />
            </Button>
          </div>
        </div>

        {(data !== null && (
          <div className={styles.meetingCommitments}>
            {data.map((meeting) => {
              const isEditable = ['MISSED', 'ENDED'].includes(meeting.status) && groups.includes('administrators');
              const attendanceData = meeting.attendances
                .map((a) => {
                  const teamMember = meeting.team.teamMembers.filter((t) => t.cognitoId === a.cognitoId)?.[0];
                  const { firstName = '?', lastName = '?' } = teamMember || {};

                  return {
                    ...a,
                    portrait: <DummyUserPortrait cognitoId={a.cognitoId} firstName={firstName} lastName={lastName} />,
                    firstName,
                    lastName,
                  };
                })
                .sort(attendanceSortFn);

              return attendanceData
                .map(({ cognitoId, firstName, lastName, attendance }) => {
                  const commitments = meeting.commitments
                    .filter((c) => c.cognitoId === cognitoId)
                    .map((c) => ({
                      ...c,
                      commitment: (
                        <CommitmentsTableCommitment
                          isGoalAgnostic={c.isGoalAgnostic}
                          label={c.label}
                          note={c.note}
                          goal={c.goal?.label || null}
                          startValue={c.startValue}
                          endValue={c.endValue}
                        />
                      ),
                    }));

                  return (
                    <CommitmentsTeamMember
                      key={uuid()}
                      cognitoId={cognitoId}
                      firstName={firstName}
                      lastName={lastName}
                      commitments={commitments}
                      attendance={attendance}
                      isEditable={isEditable}
                      addCommitment={() => {
                        setSelectedMeeting(meeting);
                        setSelectedCommitment({
                          label: '',
                          note: '',
                          startValue: '',
                          endValue: '',
                          value: '',
                          isAligned: false,
                          isTimely: false,
                          isImpactful: false,
                          isSpecific: false,
                          goalId: null,
                          cognitoId,
                          meetingId: meeting.id,
                          teamId: meeting.team.id,
                        });
                        setIsEditMeetingCommitmentDialogOpen(true);
                      }}
                      editCommitment={(commitment) => {
                        setSelectedMeeting(meeting);
                        setSelectedCommitment(commitment);
                        setIsEditMeetingCommitmentDialogOpen(true);
                      }}
                      additionalHeaderContent={
                        <>
                          <div className={styles.team}>
                            <GroupsOutlined />
                            <Link to={`/administrator/teams/${meeting.team.id}`}>{meeting.team.label}</Link>
                          </div>
                          <div className={styles.meeting}>
                            <LaunchIcon fontSize="small" />
                            <Link to={`/administrator/teams/${meeting.team.id}/meetings/${meeting.id}/details`}>
                              View meeting
                            </Link>
                          </div>
                        </>
                      }
                    />
                  );
                })
                .flat();
            })}
          </div>
        )) || (
          <Error
            icon={<SearchRoundedIcon fontSize="inherit" />}
            title="Could not find commitments for this week"
            text="There were no commitments created during meetings that happened within the selected time window."
          />
        )}
      </div>
    </>
  );
}
